import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, ButtonPrimitive, Tabs, useManagedTabs } from '@figma/fpl-components';
import { Icon16ChevronLeft, Icon16ChevronRight } from '@figma/fpl-icons';
import { borderRadius, color as fplColor, spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { TextSmRg, TextStdRg } from '@/UI/AppTypography/AppTypography';
import AppDivider from '@/UI/AppDivider/AppDivider';
import { I18N_KEYS } from '@/language/keys';
import { FolderScope } from '@/enums/folder-scope.enum';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import type { FolderResponseDto } from '@/types/folder.types';

export interface MoveModalFplContentProps {
  folders: FolderResponseDto[];
  showWorkspaceOption: boolean;
  selectedScope: FolderScope;
  onTabChange: (scope: FolderScope) => void;
  fplStack: FolderResponseDto[];
  selectedFolderId: string | null;
  onFolderSelect: (folder: FolderResponseDto) => void;
  onFolderEnter: (folder: FolderResponseDto) => void;
  onBreadcrumbNavigate: (depth: number) => void;
  currentFolderId?: string | null;
  itemId: string | null;
  isFolder: boolean;
}

const folderRowStyles = stylex.create({
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    borderRadius: borderRadius.medium,
    cursor: 'pointer',
    textAlign: 'start',
    backgroundColor: {
      default: 'transparent',
      ':hover': fplColor.bgHover,
    },
    '--enter-btn-opacity': {
      default: '0',
      ':hover': '1',
      ':focus-within': '1',
    },
  },
  rowSelected: {
    backgroundColor: {
      default: fplColor.bgSelected,
      ':hover': fplColor.bgSelected,
    },
  },
  enterButton: {
    marginRight: spacer.spacer4px,
    opacity: 'var(--enter-btn-opacity, 0)',
  },
});

const breadcrumbStyles = stylex.create({
  crumb: {
    display: 'inline-flex',
    alignItems: 'center',
    border: 'none',
    paddingBlock: spacer.spacer0px,
    paddingInline: spacer.spacer4px,
    borderRadius: borderRadius.medium,
    cursor: 'pointer',
    color: fplColor.textSecondary,
    backgroundColor: {
      default: 'transparent',
      ':hover': fplColor.bgHover,
    },
  },
  crumbCurrent: {
    display: 'inline-flex',
    alignItems: 'center',
    paddingBlock: spacer.spacer0px,
    paddingInline: spacer.spacer4px,
    color: fplColor.text,
  },
  separator: {
    marginInline: spacer.spacer4px,
  },
});

const layoutStyles = stylex.create({
  breadcrumbBar: {
    gap: spacer.spacer4px,
    padding: spacer.spacer8px,
  },
  breadcrumbCrumbs: {
    flexWrap: 'wrap',
  },
  folderList: {
    maxHeight: '300px',
    overflowY: 'auto',
    padding: spacer.spacer8px,
  },
  folderRowLabel: {
    gap: spacer.spacer8px,
    paddingBlock: spacer.spacer8px,
    paddingInline: spacer.spacer12px,
    flex: 1,
    minWidth: 0,
  },
});

export const MoveModalFplContent = ({
  folders,
  showWorkspaceOption,
  selectedScope,
  onTabChange,
  fplStack,
  selectedFolderId,
  onFolderSelect,
  onFolderEnter,
  onBreadcrumbNavigate,
  currentFolderId,
  itemId,
  isFolder,
}: MoveModalFplContentProps) => {
  const { t } = useTranslation();

  const tabIds: Record<FolderScope, boolean> = {
    [FolderScope.PERSONAL]: true,
    [FolderScope.WORKSPACE]: showWorkspaceOption,
  };
  const [tabProps, , tabManager] = useManagedTabs<FolderScope>(tabIds, selectedScope, onTabChange);

  const currentParentId = fplStack.length > 0 ? fplStack[fplStack.length - 1].id : null;
  const displayFolders = useMemo(
    () => folders.filter((folder) => folder.parentId === currentParentId && !(isFolder && folder.id === itemId)),
    [folders, currentParentId, isFolder, itemId],
  );

  const atRoot = fplStack.length === 0;
  const scopeName =
    selectedScope === FolderScope.PERSONAL
      ? t(I18N_KEYS.NAVIGATION_DRAWER.MY_FILES)
      : t(I18N_KEYS.NAVIGATION_DRAWER.WORKSPACE_FILES);

  return (
    <FlexCol>
      {atRoot ? (
        <div style={{ padding: '8px' }}>
          <Tabs.TabStrip manager={tabManager}>
            <Tabs.Tab {...tabProps[FolderScope.PERSONAL]}>{t(I18N_KEYS.NAVIGATION_DRAWER.MY_FILES)}</Tabs.Tab>
            {showWorkspaceOption && (
              <Tabs.Tab {...tabProps[FolderScope.WORKSPACE]}>{t(I18N_KEYS.NAVIGATION_DRAWER.WORKSPACE_FILES)}</Tabs.Tab>
            )}
          </Tabs.TabStrip>
        </div>
      ) : (
        <FlexCenVer {...stylex.props(layoutStyles.breadcrumbBar)}>
          <AppIconButton
            aria-label={t(I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.BACK_ARIA)}
            onClick={() => onBreadcrumbNavigate(fplStack.length - 1)}
          >
            <Icon16ChevronLeft />
          </AppIconButton>
          <FlexCenVer {...stylex.props(layoutStyles.breadcrumbCrumbs)}>
            <ButtonPrimitive onClick={() => onBreadcrumbNavigate(0)} {...stylex.props(breadcrumbStyles.crumb)}>
              <TextSmRg>{scopeName}</TextSmRg>
            </ButtonPrimitive>
            {fplStack.map((folder, index) => {
              const isLast = index === fplStack.length - 1;
              return (
                <FlexCenVer key={folder.id}>
                  <span {...stylex.props(breadcrumbStyles.separator)}>
                    <TextSmRg color={fplColor.textTertiary}>/</TextSmRg>
                  </span>
                  {isLast ? (
                    <TextSmRg aria-current="page" {...stylex.props(breadcrumbStyles.crumbCurrent)}>
                      {folder.name}
                    </TextSmRg>
                  ) : (
                    <ButtonPrimitive
                      onClick={() => onBreadcrumbNavigate(index + 1)}
                      {...stylex.props(breadcrumbStyles.crumb)}
                    >
                      <TextSmRg>{folder.name}</TextSmRg>
                    </ButtonPrimitive>
                  )}
                </FlexCenVer>
              );
            })}
          </FlexCenVer>
        </FlexCenVer>
      )}

      <AppDivider />

      <FlexCol {...stylex.props(layoutStyles.folderList)}>
        {displayFolders.map((folder) => {
          const hasChildren = folders.some(
            (child) => child.parentId === folder.id && !(isFolder && child.id === itemId),
          );
          return (
            <div
              key={folder.id}
              {...stylex.props(folderRowStyles.row, folder.id === selectedFolderId && folderRowStyles.rowSelected)}
              onClick={() => onFolderSelect(folder)}
            >
              <FlexCenVer {...stylex.props(layoutStyles.folderRowLabel)}>
                <TextStdRg color={fplColor.text}>{folder.name}</TextStdRg>
                {folder.id === currentFolderId && (
                  <Badge variant="defaultFilled">{t(I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.CURRENT_LOCATION)}</Badge>
                )}
              </FlexCenVer>
              {hasChildren && (
                <span {...stylex.props(folderRowStyles.enterButton)}>
                  <AppIconButton
                    aria-label={t(I18N_KEYS.GENERAL.ARIA.OPEN_NAMED_ITEM, { name: folder.name })}
                    onClick={(e) => {
                      e.stopPropagation();
                      onFolderEnter(folder);
                    }}
                  >
                    <Icon16ChevronRight />
                  </AppIconButton>
                </span>
              )}
            </div>
          );
        })}
      </FlexCol>
    </FlexCol>
  );
};
